import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { diffChars, diffWords, diffLines } from "diff";
import * as pdfjsLib from "pdfjs-dist/build/pdf";

pdfjsLib.GlobalWorkerOptions.workerSrc =
  `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;

/* ── helpers ─────────────────────────────────────────────────────── */

function getCI(obj, keys) {
  if (!obj) return undefined;
  const map = new Map(Object.keys(obj).map(k => [k.toLowerCase(), k]));
  for (const k of keys) {
    const real = map.get(String(k).toLowerCase());
    if (real !== undefined && obj[real] != null) return obj[real];
  }
  return undefined;
}
function getDocName(doc) {
  return getCI(doc, ["originalName", "fileName", "documentName", "name", "originalFileName"]) || "Untitled";
}
function getExt(doc) {
  return (getCI(doc, ["fileExtension", "extension", "type"]) || "").toString().replace(/^\./, "").toUpperCase() || "DOC";
}
function getDocLink(doc) {
  return getCI(doc, ["itemId", "ItemId", "link", "Link"]) || "";
}
function fmtValue(v) {
  if (v == null || v === "") return "-";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "object") {
    try { return JSON.stringify(v, null, 2); } catch { return String(v); }
  }
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) {
    try { return new Date(s).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }); } catch {}
  }
  return s;
}

const SKIP_KEYS = new Set(["searchexpression", "requestid", "responseid", "raw", "dbname", "database", "db"]);
const FIELD_ORDER = ["Id", "ArchiveNo", "OriginalFileName", "FileExtension", "DocumentDate", "PageCount", "CompressType"];

/* ── fetch PDF ArrayBuffer ──────────────────────────────────────── */

async function fetchDocBuffer(doc, token, apiBase) {
  const link = getDocLink(doc);
  if (!link) return null;
  const resp = await fetch(`${apiBase}/api/Document/ViewDocument`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ requests: [{ requestType: 0, link, outputFormat: "PDF" }] }),
  });
  if (!resp.ok) return null;
  return await resp.arrayBuffer();
}

/* ── render a single PDF page to an offscreen canvas ─────────────── */

async function renderPageToCanvas(pdfDoc, pageNum, scale) {
  const page = await pdfDoc.getPage(pageNum);
  const vp = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  canvas.width = vp.width;
  canvas.height = vp.height;
  const ctx = canvas.getContext("2d");
  await page.render({ canvasContext: ctx, viewport: vp }).promise;
  return canvas;
}

/* ── extract text from a PDF page ────────────────────────────────── */

async function extractPageText(pdfDoc, pageNum) {
  const page = await pdfDoc.getPage(pageNum);
  const content = await page.getTextContent();
  let lines = [];
  let lastY = null;
  let line = "";
  for (const item of content.items) {
    const y = Math.round(item.transform[5]);
    if (lastY !== null && Math.abs(y - lastY) > 3) {
      lines.push(line);
      line = "";
    }
    line += item.str;
    lastY = y;
  }
  if (line) lines.push(line);
  return lines;
}

/* ── pixel diff between two canvases ─────────────────────────────── */

function computePixelDiff(canvasA, canvasB) {
  const w = Math.max(canvasA.width, canvasB.width);
  const h = Math.max(canvasA.height, canvasB.height);

  // Create result canvases for overlay
  const diffCanvas = document.createElement("canvas");
  diffCanvas.width = w;
  diffCanvas.height = h;
  const diffCtx = diffCanvas.getContext("2d");

  const ctxA = canvasA.getContext("2d");
  const ctxB = canvasB.getContext("2d");
  const dataA = ctxA.getImageData(0, 0, canvasA.width, canvasA.height);
  const dataB = ctxB.getImageData(0, 0, canvasB.width, canvasB.height);
  const diffData = diffCtx.createImageData(w, h);

  let changedPixels = 0;
  const totalPixels = w * h;
  const threshold = 30; // color difference threshold

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const idx = (y * w + x) * 4;
      // Get pixel from A
      const inA = x < canvasA.width && y < canvasA.height;
      const idxA = inA ? (y * canvasA.width + x) * 4 : -1;
      const rA = inA ? dataA.data[idxA] : 255;
      const gA = inA ? dataA.data[idxA + 1] : 255;
      const bA = inA ? dataA.data[idxA + 2] : 255;

      // Get pixel from B
      const inB = x < canvasB.width && y < canvasB.height;
      const idxB = inB ? (y * canvasB.width + x) * 4 : -1;
      const rB = inB ? dataB.data[idxB] : 255;
      const gB = inB ? dataB.data[idxB + 1] : 255;
      const bB = inB ? dataB.data[idxB + 2] : 255;

      const dr = Math.abs(rA - rB);
      const dg = Math.abs(gA - gB);
      const db = Math.abs(bA - bB);
      const diff = dr + dg + db;

      if (diff > threshold) {
        // Red highlight for differences
        diffData.data[idx] = 255;
        diffData.data[idx + 1] = 60;
        diffData.data[idx + 2] = 60;
        diffData.data[idx + 3] = 140;
        changedPixels++;
      } else {
        // Transparent
        diffData.data[idx + 3] = 0;
      }
    }
  }

  diffCtx.putImageData(diffData, 0, 0);
  const changePercent = totalPixels > 0 ? ((changedPixels / totalPixels) * 100).toFixed(1) : 0;
  return { diffCanvas, changePercent, changedPixels };
}

/* ── inline text diff renderer ───────────────────────────────────── */

function DiffText({ left, right }) {
  if (left === right) return <span className="cv-val">{left}</span>;
  const diffFn = (left.length + right.length) > 120 ? diffWords : diffChars;
  const parts = diffFn(left, right);
  return (
    <span className="cv-val cv-val--diff">
      {parts.map((p, i) =>
        p.added ? <span key={i} className="cv-diff-add">{p.value}</span>
        : p.removed ? <span key={i} className="cv-diff-del">{p.value}</span>
        : <span key={i}>{p.value}</span>
      )}
    </span>
  );
}

/* ── field row (metadata) ────────────────────────────────────────── */

function FieldRow({ fieldKey, values, isDiff, baseIdx, showDiffInline }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = values.some(v => v.length > 100);
  return (
    <tr className={`cv-row${isDiff ? " cv-row--diff" : ""}`}>
      <td className="cv-field-name">
        <span>{fieldKey}</span>
        {isDiff && <span className="cv-diff-badge" />}
      </td>
      {values.map((v, i) => (
        <td key={i} className={`cv-cell${isDiff && v !== values[baseIdx] ? " cv-cell--changed" : ""}`}>
          {isDiff && showDiffInline && i !== baseIdx ? (
            <DiffText left={values[baseIdx]} right={v} />
          ) : isLong ? (
            <div className={`cv-long-val${expanded ? " cv-long-val--open" : ""}`}>
              <span className="cv-val">{expanded ? v : v.slice(0, 100) + "…"}</span>
              <button className="cv-expand-btn" onClick={() => setExpanded(e => !e)}>{expanded ? "▾" : "▸"}</button>
            </div>
          ) : (
            <span className="cv-val">{v}</span>
          )}
        </td>
      ))}
    </tr>
  );
}

/* ── PDF page canvas viewer ──────────────────────────────────────── */

function PageCanvas({ canvas, overlay, zoom }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.innerHTML = "";
    if (!canvas) return;

    const wrap = document.createElement("div");
    wrap.style.position = "relative";
    wrap.style.display = "inline-block";
    wrap.style.transformOrigin = "top left";
    wrap.style.transform = `scale(${zoom})`;

    const c = canvas.cloneNode(true);
    const ctx = c.getContext("2d");
    ctx.drawImage(canvas, 0, 0);
    c.style.display = "block";
    wrap.appendChild(c);

    if (overlay) {
      const o = document.createElement("canvas");
      o.width = overlay.width;
      o.height = overlay.height;
      const octx = o.getContext("2d");
      octx.drawImage(overlay, 0, 0);
      o.style.position = "absolute";
      o.style.top = "0";
      o.style.left = "0";
      o.style.pointerEvents = "none";
      wrap.appendChild(o);
    }

    el.appendChild(wrap);
  }, [canvas, overlay, zoom]);

  return <div ref={containerRef} className="cv-page-canvas-container" />;
}

/* ── Text diff row ───────────────────────────────────────────────── */

function TextDiffTable({ textA, textB, t }) {
  const diffs = useMemo(() => {
    const linesA = (textA || []).join("\n");
    const linesB = (textB || []).join("\n");
    if (linesA === linesB) return null;
    return diffLines(linesA, linesB);
  }, [textA, textB]);

  if (!diffs) {
    return (
      <div className="cv-text-diff-empty">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6L9 17l-5-5" /></svg>
        {t("compare.textIdentical")}
      </div>
    );
  }

  let lineA = 1, lineB = 1;
  return (
    <div className="cv-text-diff-wrap">
      <table className="cv-text-diff-table">
        <thead>
          <tr>
            <th className="cv-tdiff-ln">#</th>
            <th className="cv-tdiff-col">{t("compare.docA")}</th>
            <th className="cv-tdiff-col">{t("compare.docB")}</th>
          </tr>
        </thead>
        <tbody>
          {diffs.map((part, i) => {
            const lines = part.value.split("\n").filter((_, idx, arr) => idx < arr.length - 1 || _ !== "");
            return lines.map((line, j) => {
              const key = `${i}-${j}`;
              let numA = "", numB = "";
              let cls = "";
              if (part.removed) {
                numA = lineA++;
                cls = "cv-tdiff-row--del";
              } else if (part.added) {
                numB = lineB++;
                cls = "cv-tdiff-row--add";
              } else {
                numA = lineA++;
                numB = lineB++;
              }
              return (
                <tr key={key} className={cls}>
                  <td className="cv-tdiff-ln">{numA || numB}</td>
                  <td className={`cv-tdiff-cell${part.removed ? " cv-tdiff-cell--del" : ""}`}>
                    {part.added ? "" : line}
                  </td>
                  <td className={`cv-tdiff-cell${part.added ? " cv-tdiff-cell--add" : ""}`}>
                    {part.removed ? "" : line}
                  </td>
                </tr>
              );
            });
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════ */
/* ══  MAIN COMPONENT  ════════════════════════════════════════════ */
/* ══════════════════════════════════════════════════════════════════ */

export default function CompareView({ documents, onClose, onRemove, token, apiBase }) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("visual");
  const [showOnlyDiffs, setShowOnlyDiffs] = useState(false);
  const [baseIdx, setBaseIdx] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDiffOverlay, setShowDiffOverlay] = useState(true);

  // PDF processing state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageCountA, setPageCountA] = useState(0);
  const [pageCountB, setPageCountB] = useState(0);
  const [pdfDocA, setPdfDocA] = useState(null);
  const [pdfDocB, setPdfDocB] = useState(null);

  // Rendered page state
  const [canvasA, setCanvasA] = useState(null);
  const [canvasB, setCanvasB] = useState(null);
  const [diffOverlay, setDiffOverlay] = useState(null);
  const [changePercent, setChangePercent] = useState(0);
  const [textA, setTextA] = useState(null);
  const [textB, setTextB] = useState(null);
  const [pageLoading, setPageLoading] = useState(false);

  const RENDER_SCALE = 1.5;
  const docA = documents[0];
  const docB = documents.length > 1 ? documents[1] : null;

  // ── Load PDFs on mount ──
  useEffect(() => {
    if (!token || !apiBase || !docA) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const bufs = await Promise.all(
          [docA, docB].filter(Boolean).map(d => fetchDocBuffer(d, token, apiBase))
        );
        if (cancelled) return;
        if (!bufs[0]) { setError("couldNotLoadA"); setLoading(false); return; }

        const pA = await pdfjsLib.getDocument({ data: bufs[0] }).promise;
        if (cancelled) return;
        setPdfDocA(pA);
        setPageCountA(pA.numPages);

        if (bufs[1]) {
          const pB = await pdfjsLib.getDocument({ data: bufs[1] }).promise;
          if (cancelled) return;
          setPdfDocB(pB);
          setPageCountB(pB.numPages);
        }
        setLoading(false);
      } catch (e) {
        if (!cancelled) { setError(e.message || "loadError"); setLoading(false); }
      }
    })();

    return () => { cancelled = true; };
  }, [docA, docB, token, apiBase]);

  // ── Render current page pair ──
  useEffect(() => {
    if (!pdfDocA) return;
    let cancelled = false;

    (async () => {
      setPageLoading(true);
      try {
        const cA = currentPage <= pdfDocA.numPages
          ? await renderPageToCanvas(pdfDocA, currentPage, RENDER_SCALE)
          : null;
        if (cancelled) return;
        setCanvasA(cA);

        const tA = currentPage <= pdfDocA.numPages
          ? await extractPageText(pdfDocA, currentPage)
          : [];
        if (cancelled) return;
        setTextA(tA);

        if (pdfDocB && currentPage <= pdfDocB.numPages) {
          const cB = await renderPageToCanvas(pdfDocB, currentPage, RENDER_SCALE);
          if (cancelled) return;
          setCanvasB(cB);

          const tB = await extractPageText(pdfDocB, currentPage);
          if (cancelled) return;
          setTextB(tB);

          // Compute pixel diff
          if (cA && cB) {
            const { diffCanvas, changePercent: cp } = computePixelDiff(cA, cB);
            if (!cancelled) {
              setDiffOverlay(diffCanvas);
              setChangePercent(cp);
            }
          }
        } else {
          setCanvasB(null);
          setTextB([]);
          setDiffOverlay(null);
          setChangePercent(0);
        }
      } catch (e) {
        console.error("Page render error:", e);
      }
      if (!cancelled) setPageLoading(false);
    })();

    return () => { cancelled = true; };
  }, [pdfDocA, pdfDocB, currentPage]);

  const maxPages = Math.max(pageCountA, pageCountB);

  // ── Metadata diff ──
  const { fieldRows, diffCount, totalCount } = useMemo(() => {
    const keySet = new Set();
    for (const doc of documents) {
      if (!doc) continue;
      for (const k of Object.keys(doc)) {
        if (!SKIP_KEYS.has(k.toLowerCase())) keySet.add(k);
      }
    }
    const ordered = [...keySet].sort((a, b) => {
      const ai = FIELD_ORDER.indexOf(a);
      const bi = FIELD_ORDER.indexOf(b);
      if (ai >= 0 && bi >= 0) return ai - bi;
      if (ai >= 0) return -1;
      if (bi >= 0) return 1;
      return a.localeCompare(b);
    });
    let dc = 0;
    const rows = ordered.map(key => {
      const formatted = documents.map(doc => fmtValue(doc?.[key]));
      const allSame = formatted.every(v => v === formatted[0]);
      if (!allSame) dc++;
      return { key, values: formatted, isDiff: !allSame };
    });
    return { fieldRows: rows, diffCount: dc, totalCount: rows.length };
  }, [documents]);

  const visibleRows = useMemo(() =>
    showOnlyDiffs ? fieldRows.filter(r => r.isDiff) : fieldRows
  , [fieldRows, showOnlyDiffs]);

  const handleBaseChange = useCallback((idx) => setBaseIdx(idx), []);
  const handleZoom = useCallback((delta) => {
    setZoom(z => Math.min(3, Math.max(0.25, +(z + delta).toFixed(2))));
  }, []);

  return createPortal(
    <div className="compare-overlay" onClick={onClose}>
      <div className="cv-panel cv-panel--full" onClick={e => e.stopPropagation()}>
        {/* ── Header ── */}
        <div className="cv-header">
          <div className="cv-header-left">
            <h2>{t("compare.title")}</h2>
            <div className="cv-tabs">
              <button className={`cv-tab${activeTab === "visual" ? " cv-tab--active" : ""}`} onClick={() => setActiveTab("visual")}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
                </svg>
                {t("compare.visualTab")}
              </button>
              <button className={`cv-tab${activeTab === "metadata" ? " cv-tab--active" : ""}`} onClick={() => setActiveTab("metadata")}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
                  <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
                </svg>
                {t("compare.metadataTab")}
                {diffCount > 0 && <span className="cv-tab-badge">{diffCount}</span>}
              </button>
            </div>
          </div>
          <div className="cv-header-right">
            {activeTab === "visual" && (
              <>
                {/* Page nav */}
                {maxPages > 1 && (
                  <div className="cv-page-nav">
                    <button className="cv-zoom-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
                    </button>
                    <span className="cv-page-label">{currentPage} / {maxPages}</span>
                    <button className="cv-zoom-btn" onClick={() => setCurrentPage(p => Math.min(maxPages, p + 1))} disabled={currentPage >= maxPages}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                    </button>
                  </div>
                )}
                {/* Diff overlay toggle */}
                <label className="cv-toggle">
                  <input type="checkbox" checked={showDiffOverlay} onChange={e => setShowDiffOverlay(e.target.checked)} />
                  <span className="cv-toggle-slider" />
                  <span className="cv-toggle-label">{t("compare.showDiffOverlay")}</span>
                </label>
                {/* Zoom */}
                <div className="cv-zoom-controls">
                  <button className="cv-zoom-btn" onClick={() => handleZoom(-0.15)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                  </button>
                  <span className="cv-zoom-label">{Math.round(zoom * 100)}%</span>
                  <button className="cv-zoom-btn" onClick={() => handleZoom(0.15)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                  </button>
                  <button className="cv-zoom-btn" onClick={() => setZoom(1)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 4v6h6"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
                  </button>
                </div>
              </>
            )}
            {activeTab === "metadata" && (
              <label className="cv-toggle">
                <input type="checkbox" checked={showOnlyDiffs} onChange={e => setShowOnlyDiffs(e.target.checked)} />
                <span className="cv-toggle-slider" />
                <span className="cv-toggle-label">{t("compare.onlyDifferences")}</span>
              </label>
            )}
            <button className="compare-close-btn" onClick={onClose}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>

        {/* ════════════════════════════════════════════════════════ */}
        {/* ═══  VISUAL TAB  ══════════════════════════════════════ */}
        {/* ════════════════════════════════════════════════════════ */}
        {activeTab === "visual" && (
          <div className="cv-visual-body">
            {loading && (
              <div className="cv-preview-loading">
                <div className="cv-spinner" />
                <span>{t("compare.loadingDoc")}</span>
              </div>
            )}
            {error && (
              <div className="cv-preview-error">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
                </svg>
                <span>{t("compare.previewError")}</span>
              </div>
            )}
            {!loading && !error && (
              <>
                {/* Side-by-side rendered pages */}
                <div className="cv-preview-container">
                  {/* Doc A */}
                  <div className="cv-preview-pane">
                    <div className="cv-preview-pane-header">
                      <span className={`rp-file-badge ${getExt(docA).toLowerCase()}`}>.{getExt(docA).toLowerCase()}</span>
                      <span className="cv-preview-pane-name" title={getDocName(docA)}>{getDocName(docA)}</span>
                      <span className="cv-base-tag">{t("compare.docA")}</span>
                      <span className="cv-page-info">{t("compare.pageOf", { current: currentPage, total: pageCountA })}</span>
                    </div>
                    <div className="cv-preview-frame-wrap">
                      {pageLoading && <div className="cv-preview-loading"><div className="cv-spinner" /></div>}
                      {canvasA && <PageCanvas canvas={canvasA} overlay={showDiffOverlay ? diffOverlay : null} zoom={zoom} />}
                      {!canvasA && !pageLoading && currentPage > pageCountA && (
                        <div className="cv-preview-empty"><span>{t("compare.noPage")}</span></div>
                      )}
                    </div>
                  </div>
                  {/* Doc B */}
                  {docB && (
                    <div className="cv-preview-pane">
                      <div className="cv-preview-pane-header">
                        <span className={`rp-file-badge ${getExt(docB).toLowerCase()}`}>.{getExt(docB).toLowerCase()}</span>
                        <span className="cv-preview-pane-name" title={getDocName(docB)}>{getDocName(docB)}</span>
                        <span className="cv-base-tag cv-base-tag--b">{t("compare.docB")}</span>
                        <span className="cv-page-info">{t("compare.pageOf", { current: currentPage, total: pageCountB })}</span>
                        {changePercent > 0 && (
                          <span className="cv-change-badge">{changePercent}% {t("compare.changed").toLowerCase()}</span>
                        )}
                      </div>
                      <div className="cv-preview-frame-wrap">
                        {pageLoading && <div className="cv-preview-loading"><div className="cv-spinner" /></div>}
                        {canvasB && <PageCanvas canvas={canvasB} overlay={showDiffOverlay ? diffOverlay : null} zoom={zoom} />}
                        {!canvasB && !pageLoading && currentPage > pageCountB && (
                          <div className="cv-preview-empty"><span>{t("compare.noPage")}</span></div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Text diff below */}
                <div className="cv-visual-diff-section">
                  <div className="cv-visual-diff-header">
                    <span className="cv-visual-diff-title">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
                      </svg>
                      {t("compare.textDifferences")} — {t("compare.pageLabel")} {currentPage}
                    </span>
                  </div>
                  <TextDiffTable textA={textA} textB={textB} t={t} />
                </div>
              </>
            )}
          </div>
        )}

        {/* ════════════════════════════════════════════════════════ */}
        {/* ═══  METADATA TAB  ═══════════════════════════════════ */}
        {/* ════════════════════════════════════════════════════════ */}
        {activeTab === "metadata" && (
          <>
            {diffCount > 0 && (
              <div className="cv-legend">
                <span className="cv-legend-item"><span className="cv-legend-swatch cv-legend-swatch--add" />{t("compare.added")}</span>
                <span className="cv-legend-item"><span className="cv-legend-swatch cv-legend-swatch--del" />{t("compare.removed")}</span>
                <span className="cv-legend-item"><span className="cv-legend-swatch cv-legend-swatch--changed" />{t("compare.changed")}</span>
                <span className="cv-legend-sep">|</span>
                <span className="cv-legend-hint">{t("compare.baseHint")}</span>
              </div>
            )}
            <div className="cv-table-wrap">
              <table className="cv-table">
                <thead>
                  <tr>
                    <th className="cv-th-field">{t("compare.field")}</th>
                    {documents.map((doc, i) => (
                      <th key={i} className={`cv-th-doc${i === baseIdx ? " cv-th-doc--base" : ""}`}>
                        <div className="cv-doc-header">
                          <button className={`cv-base-radio${i === baseIdx ? " cv-base-radio--active" : ""}`} onClick={() => handleBaseChange(i)} title={t("compare.setAsBase")}>
                            {i === baseIdx && <span className="cv-base-dot" />}
                          </button>
                          <span className={`rp-file-badge ${getExt(doc).toLowerCase()}`}>.{getExt(doc).toLowerCase()}</span>
                          <span className="cv-doc-name" title={getDocName(doc)}>{getDocName(doc)}</span>
                          {i === baseIdx && <span className="cv-base-tag">{t("compare.base")}</span>}
                          {onRemove && documents.length > 2 && (
                            <button className="compare-remove-btn" onClick={() => onRemove(doc)} title={t("compare.remove")}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map(({ key, values, isDiff }) => (
                    <FieldRow key={key} fieldKey={key} values={values} isDiff={isDiff} baseIdx={baseIdx} showDiffInline={documents.length === 2} />
                  ))}
                  {visibleRows.length === 0 && (
                    <tr><td colSpan={documents.length + 1} className="cv-empty">{showOnlyDiffs ? t("compare.noDifferences") : t("compare.noFields")}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Footer */}
        <div className="cv-footer">
          <div className="cv-footer-info">
            {activeTab === "visual" && changePercent > 0 && (
              <span className="cv-footer-diff">{changePercent}% {t("compare.pixelDiff")}</span>
            )}
            {activeTab === "visual" && changePercent === 0 && !pageLoading && canvasA && canvasB && (
              <span className="cv-footer-match">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6L9 17l-5-5"/></svg>
                {t("compare.pagesIdentical")}
              </span>
            )}
            {activeTab === "metadata" && (
              <>
                <span className="cv-stats-total">{t("compare.totalFields", { count: totalCount })}</span>
                <span className="cv-footer-sep">·</span>
                {diffCount === 0
                  ? <span className="cv-footer-match"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6L9 17l-5-5"/></svg>{t("compare.noDifferences")}</span>
                  : <span className="cv-footer-diff">{t("compare.summaryDiffs", { diff: diffCount, total: totalCount })}</span>
                }
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
